import { assertTargetSpec } from '../targeting/enumerate.js';
/** Все допустимые значения CardType (для валидации формы). */
const CARD_TYPES = Object.freeze([
    'UNIT',
    'SPELL',
    'STRUCTURE',
    'REACTION',
    'VIRUS',
    'EVENT',
    'ITEM',
    'EFFECT',
]);
const CARD_KEYWORDS = Object.freeze(['RUSH', 'CANNOT_ATTACK', 'CANNOT_COOL']);
const CARD_TRAITS = Object.freeze(['SLIME', 'PLANT', 'TREE', 'LEAF']);
/** Известные имена хуков (для валидации формы hooks — ловим опечатки автора). */
const HOOK_NAMES = Object.freeze([
    'onPlay',
    'onEnterPlay',
    'onLeavePlay',
    'onDeath',
    'onBeforeCombat',
    'onDealDamage',
    'onTakeDamage',
    'onAttack',
    'onAttackResolved',
    'onAttacked',
    'onAfterCombat',
    'beforeOwnAttack',
    'interceptAttack',
    'beforeAlliedAttack',
    'interceptIncomingDamage',
    'modifyOutgoingDamage',
    'modifyIncomingDamage',
    'replaceDamage',
    'planRandomDamageReplacement',
    'replaceDeath',
    'planReactionTrigger',
    'onHackTriggered',
    'onRoundStart',
    'onRoundEnd',
    'ongoing',
    'onAnyEvent',
]);
/** Guard: конечное неотрицательное целое (для baseStats/cost). */
function assertNonNegativeInt(x, what) {
    if (typeof x !== 'number' || !Number.isInteger(x) || x < 0) {
        throw new Error(`defineCard: ${what} должен быть неотрицательным целым (получено ${String(x)})`);
    }
}
/**
 * Тонкая фабрика: валидирует ФОРМУ CardDefinition и возвращает его как есть
 * (tech/22 §2). НЕ содержит логики механик — только помогает типам/регистрации.
 */
export function defineCard(def) {
    if (typeof def.id !== 'string' || def.id === '') {
        throw new Error(`defineCard: id должен быть непустой строкой (получено ${JSON.stringify(def.id)})`);
    }
    if (!CARD_TYPES.includes(def.type)) {
        throw new Error(`defineCard: тип "${String(def.type)}" неизвестен (ожидался ${CARD_TYPES.join(' | ')}) для карты "${def.id}"`);
    }
    if (def.type === 'UNIT') {
        if (def.baseStats === undefined) {
            throw new Error(`defineCard: у UNIT "${def.id}" обязателен baseStats { attack, health }`);
        }
        assertNonNegativeInt(def.baseStats.attack, `baseStats.attack карты "${def.id}"`);
        assertNonNegativeInt(def.baseStats.health, `baseStats.health карты "${def.id}"`);
    }
    else if (def.baseStats !== undefined) {
        // Форму допускаем и для не-UNIT (type-agnostic), но валидируем числа.
        assertNonNegativeInt(def.baseStats.attack, `baseStats.attack карты "${def.id}"`);
        assertNonNegativeInt(def.baseStats.health, `baseStats.health карты "${def.id}"`);
    }
    if (def.cost !== undefined) {
        assertNonNegativeInt(def.cost, `cost карты "${def.id}"`);
    }
    if (def.minimumCost !== undefined) {
        assertNonNegativeInt(def.minimumCost, `minimumCost карты "${def.id}"`);
        if (def.cost !== undefined && def.minimumCost > def.cost) {
            throw new Error(`defineCard: minimumCost карты "${def.id}" не может быть выше cost`);
        }
    }
    if (def.effectiveCost !== undefined && typeof def.effectiveCost !== 'function') {
        throw new Error(`defineCard: effectiveCost карты "${def.id}" должен быть функцией`);
    }
    if (def.keywords !== undefined) {
        if (new Set(def.keywords).size !== def.keywords.length || def.keywords.some((keyword) => !CARD_KEYWORDS.includes(keyword))) {
            throw new Error(`defineCard: keywords карты "${def.id}" содержит неизвестное или повторное значение`);
        }
    }
    if (def.traits !== undefined) {
        if (new Set(def.traits).size !== def.traits.length || def.traits.some((trait) => !CARD_TRAITS.includes(trait))) {
            throw new Error(`defineCard: traits карты "${def.id}" содержит неизвестное или повторное значение`);
        }
    }
    if (def.combatDamageParts !== undefined) {
        if (!Number.isInteger(def.combatDamageParts) || def.combatDamageParts < 1) {
            throw new Error(`defineCard: combatDamageParts карты "${def.id}" должен быть положительным целым ` +
                `(получено ${String(def.combatDamageParts)})`);
        }
    }
    if (def.combatDamageOverflow !== undefined && def.combatDamageOverflow !== 'to_player') {
        throw new Error(`defineCard: combatDamageOverflow карты "${def.id}" содержит неизвестное значение`);
    }
    if (def.combatDamageOverflow !== undefined && (def.combatDamageParts ?? 1) < 2) {
        throw new Error(`defineCard: combatDamageOverflow карты "${def.id}" требует не менее двух combatDamageParts`);
    }
    if (def.type === 'STRUCTURE') {
        if (def.baseStats !== undefined && def.baseStats.attack !== 0) {
            throw new Error(`defineCard: STRUCTURE "${def.id}" должна иметь baseStats.attack = 0`);
        }
    }
    if (def.planning !== undefined) {
        if (def.planning.target !== undefined && typeof def.planning.target === 'string'
            && !['none', 'unit', 'cell', 'unit+cell'].includes(def.planning.target)) {
            throw new Error(`defineCard: planning.target карты "${def.id}" неизвестен`);
        }
        if (def.planning.target === undefined && def.planning.targetByMode === undefined && def.planning.steps === undefined) {
            throw new Error(`defineCard: planning карты "${def.id}" требует target или targetByMode`);
        }
        if (def.planning.target !== undefined && typeof def.planning.target === 'object' && def.planning.steps !== undefined) {
            throw new Error(`defineCard: planning карты "${def.id}" не может одновременно задавать target spec и steps`);
        }
        if (def.planning.target !== undefined && typeof def.planning.target === 'object') {
            assertTargetSpec(def.planning.target);
        }
        if (Array.isArray(def.planning.steps)) {
            const ids = def.planning.steps.map((step) => step.stepId);
            if (ids.some((id) => typeof id !== 'string' || id === '') || new Set(ids).size !== ids.length) {
                throw new Error(`defineCard: planning.steps карты "${def.id}" требует уникальные непустые stepId`);
            }
            for (const step of def.planning.steps) {
                assertTargetSpec(step.spec);
                if (step.onInvalidAtResolution !== undefined
                    && !['all_or_nothing', 'allow_partial'].includes(step.onInvalidAtResolution)) {
                    throw new Error(`defineCard: planning step policy карты "${def.id}" неизвестна`);
                }
            }
        }
        else if (def.planning.steps !== undefined && typeof def.planning.steps !== 'function') {
            throw new Error(`defineCard: planning.steps карты "${def.id}" должен быть массивом или функцией`);
        }
        if (def.planning.onInvalidAtResolution !== undefined
            && !['all_or_nothing', 'allow_partial'].includes(def.planning.onInvalidAtResolution)) {
            throw new Error(`defineCard: planning.onInvalidAtResolution карты "${def.id}" неизвестен`);
        }
        const placement = def.planning.fieldPlacement;
        if (placement !== undefined && placement !== 'empty'
            && (typeof placement !== 'object'
                || placement.kind !== 'overlay'
                || typeof placement.hostStepId !== 'string'
                || placement.hostStepId.length === 0)) {
            throw new Error(`defineCard: planning.fieldPlacement карты "${def.id}" неизвестен`);
        }
        if (def.planning.unitFilter !== undefined && !['ally', 'enemy'].includes(def.planning.unitFilter)) {
            throw new Error(`defineCard: planning.unitFilter карты "${def.id}" неизвестен`);
        }
        if (def.planning.validatePlan !== undefined && typeof def.planning.validatePlan !== 'function') {
            throw new Error(`defineCard: planning.validatePlan карты "${def.id}" должен быть функцией`);
        }
        if (def.planning.uniqueTargetPerRound !== undefined && typeof def.planning.uniqueTargetPerRound !== 'boolean') {
            throw new Error(`defineCard: planning.uniqueTargetPerRound карты "${def.id}" должен быть boolean`);
        }
        if (def.planning.modes !== undefined) {
            if (def.planning.modes.length === 0 || new Set(def.planning.modes).size !== def.planning.modes.length) {
                throw new Error(`defineCard: planning.modes карты "${def.id}" должен содержать уникальные режимы`);
            }
            if (def.planning.modes.some((mode) => typeof mode !== 'string' || mode === '')) {
                throw new Error(`defineCard: planning.modes карты "${def.id}" содержит пустой режим`);
            }
        }
        if (def.planning.targetByMode !== undefined) {
            if (def.planning.modes === undefined) {
                throw new Error(`defineCard: targetByMode карты "${def.id}" требует modes`);
            }
            const keys = Object.keys(def.planning.targetByMode);
            if (keys.length !== def.planning.modes.length || def.planning.modes.some((mode) => !keys.includes(mode))) {
                throw new Error(`defineCard: targetByMode карты "${def.id}" должен описывать каждый mode`);
            }
            if (Object.values(def.planning.targetByMode).some((target) => !['none', 'unit', 'cell', 'unit+cell'].includes(target))) {
                throw new Error(`defineCard: targetByMode карты "${def.id}" содержит неизвестную форму цели`);
            }
        }
    }
    if (def.hooks !== undefined) {
        if (typeof def.hooks !== 'object' || def.hooks === null) {
            throw new Error(`defineCard: hooks карты "${def.id}" должен быть объектом`);
        }
        for (const [name, value] of Object.entries(def.hooks)) {
            if (!HOOK_NAMES.includes(name)) {
                throw new Error(`defineCard: неизвестный хук "${name}" карты "${def.id}" (опечатка? список — tech/22 §3)`);
            }
            if (typeof value !== 'function') {
                throw new Error(`defineCard: хук "${name}" карты "${def.id}" должен быть функцией`);
            }
        }
    }
    return def;
}
/**
 * Собрать реестр из определений. Дубликат id — ошибка (детерминированная защита
 * от коллизий поведения). Возвращает неизменяемую мапу (ReadonlyMap).
 */
export function buildRegistry(defs) {
    const map = new Map();
    for (const def of defs) {
        if (map.has(def.id)) {
            throw new Error(`buildRegistry: дубликат id "${def.id}" — определения карт должны быть уникальны`);
        }
        map.set(def.id, def);
    }
    return map;
}
//# sourceMappingURL=registry.js.map